import React, { useState, useEffect } from 'react';
import { Table, Input, Space, Typography, Card, Tag, Button } from 'antd';
import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import InstrumentDetailDrawer from './InstrumentDetailDrawer';
import { Resizable } from 'react-resizable';
import { getInstruments } from '../services/api';
import { alexforbesBrand } from '../brand/alexforbes';
import { getCookie, setCookie } from '../utils/cookies';
import { formatDisplayDate } from '../utils/dates';
import { humanizeCode } from '../utils/labels';
import 'react-resizable/css/styles.css';

const { Title } = Typography;

// Resizable column header component
const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 10,
            cursor: 'col-resize',
            zIndex: 1,
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const InstrumentList = () => {
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: parseInt(getCookie('instrumentPageSize')) || 10,
    total: 0,
  });
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [columnWidths, setColumnWidths] = useState({
    symbol: 100,
    isin: 120,
    name: 200,
    type: 100,
    exchange: 120,
    currency: 100,
    price: 100,
    price_date: 120,
    actions: 88,
  });

  useEffect(() => {
    console.log('Fetching instruments with pagination:', pagination);
    fetchInstruments(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depend on page fields only, not whole pagination object
  }, [pagination.current, pagination.pageSize]);

  const fetchInstruments = async (page, pageSize) => {
    try {
      setLoading(true);
      console.log('Fetching instruments with page:', page, 'pageSize:', pageSize);
      const data = await getInstruments(page, pageSize);
      console.log('Received instruments data:', data);
      
      if (!data.results) {
        console.error('Expected paginated response with results but got:', typeof data);
        return;
      }
      
      setInstruments(data.results);
      setPagination(prev => ({
        ...prev,
        total: data.count || 0,
        current: page,
        pageSize: pageSize,
      }));
    } catch (error) {
      console.error('Error fetching instruments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    console.log('Table pagination changed:', newPagination);
    // Save the new page size to cookie
    if (newPagination.pageSize !== pagination.pageSize) {
      setCookie('instrumentPageSize', newPagination.pageSize);
      // Reset to first page when changing page size
      newPagination.current = 1;
    }
    // Update pagination state and trigger a new fetch
    setPagination(newPagination);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm, dataIndex) => {
    clearFilters();
    setSearchText('');
    confirm();
  };

  const getColumnSearchProps = (dataIndex, getCellText) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <button
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{ width: 90 }}
          >
            Search
          </button>
          <button onClick={() => handleReset(clearFilters, confirm, dataIndex)}>
            Reset
          </button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined style={{ color: filtered ? 'var(--primary-color)' : undefined }} />
    ),
    onFilter: (value, record) => {
      const raw = getCellText ? getCellText(record) : record[dataIndex];
      return raw
        ? raw.toString().toLowerCase().includes(value.toLowerCase())
        : false;
    },
    filteredValue: searchedColumn === dataIndex ? [searchText] : null,
  });

  const getTypeColor = (type) => {
    const c = alexforbesBrand.colors;
    switch (type?.toLowerCase()) {
      case 'stock':
        return c.teal;
      case 'etf':
        return c.orange;
      case 'bond':
        return c.tealDark;
      case 'fixed_income':
        return c.orangeLight;
      default:
        return c.neutral;
    }
  };

  const handleResize = (index) => (_, { size }) => {
    const newColumnWidths = { ...columnWidths };
    const key = columns[index].key;
    newColumnWidths[key] = size.width;
    setColumnWidths(newColumnWidths);
    
    // Update columns with new width
    const newColumns = [...columns];
    newColumns[index] = {
      ...newColumns[index],
      width: size.width,
    };
  };

  const columns = [
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      ...getColumnSearchProps('symbol'),
      sorter: (a, b) => a.symbol.localeCompare(b.symbol),
      width: columnWidths.symbol,
    },
    {
      title: 'ISIN',
      dataIndex: 'isin',
      key: 'isin',
      ...getColumnSearchProps('isin'),
      sorter: (a, b) => (a.isin || '').localeCompare(b.isin || ''),
      width: columnWidths.isin,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
      sorter: (a, b) => a.name.localeCompare(b.name),
      width: columnWidths.name,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      ...getColumnSearchProps('type'),
      filters: [
        { text: 'Stock', value: 'stock' },
        { text: 'ETF', value: 'etf' },
        { text: 'Bond', value: 'bond' },
      ],
      onFilter: (value, record) => record.type?.toLowerCase() === value,
      render: (type) => {
        if (!type) return <Tag color="default">Unknown</Tag>;
        return (
          <Tag color={getTypeColor(type)}>
            {humanizeCode(type)}
          </Tag>
        );
      },
      width: columnWidths.type,
    },
    {
      title: 'Exchange',
      dataIndex: ['exchange', 'code'],
      key: 'exchange',
      ...getColumnSearchProps('exchange', (r) => r.exchange?.code || ''),
      sorter: (a, b) => (a.exchange?.code || '').localeCompare(b.exchange?.code || ''),
      render: (_, record) => record.exchange?.code || '-',
      width: columnWidths.exchange,
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      ...getColumnSearchProps('currency'),
      sorter: (a, b) => (a.currency || '').localeCompare(b.currency || ''),
      width: columnWidths.currency,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => {
        if (!price) return '-';
        try {
          const numPrice = parseFloat(price);
          if (isNaN(numPrice)) return '-';
          const ccy = record.currency || 'EUR';
          return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: ccy,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(numPrice);
        } catch {
          return '-';
        }
      },
      sorter: (a, b) => {
        const aPrice = a.price ? parseFloat(a.price) : 0;
        const bPrice = b.price ? parseFloat(b.price) : 0;
        return aPrice - bPrice;
      },
      width: columnWidths.price,
    },
    {
      title: 'Price date',
      dataIndex: 'last_price_date',
      key: 'price_date',
      render: (value) => formatDisplayDate(value),
      sorter: (a, b) => {
        const da = new Date(a.last_price_date || 0).getTime();
        const db = new Date(b.last_price_date || 0).getTime();
        return da - db;
      },
      width: columnWidths.price_date,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: columnWidths.actions,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedInstrument(record);
            setDrawerOpen(true);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  // Add resize handlers to columns
  const resizableColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: handleResize(index),
    }),
  }));

  return (
    <div className="app-table-page">
    <Card>
      <Title level={4}>Available Instruments</Title>
      <Table
        columns={resizableColumns}
        dataSource={instruments}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
        scroll={{ x: true, y: 'calc(100vh - 300px)' }}
        sticky={{ offsetHeader: 0 }}
        style={{ marginBottom: 16 }}
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
      />
      <InstrumentDetailDrawer
        instrumentId={selectedInstrument?.id}
        initialInstrument={selectedInstrument}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedInstrument(null);
        }}
      />
    </Card>
    </div>
  );
};

export default InstrumentList; 